import { HomeAiSearchResponseModel, HomeAiSearchSettings } from "@api/useAiSearchSettingsAndAnswer";
import { useCallback, useMemo } from "react";
import { AiResultProps } from "@vms/AiResult/AiResult";
import { SettingsValue } from "@vms/AiResult/AiResultSettings";
import { useGetAppliedCategoryNamesData } from "@vms/SelectFilters/SelectFiltersContext";
import {
    useCommonDataContext,
    useGetHeatScore,
    useGetMacStage,
    useGetMetric,
    useRespondentTypesGroups
} from "@core/CommonDataContext";
import { HomeViewAs } from "@core/constants";
import { FCX, ID } from "@datassential/platform-ui-lib";
import cn from "classnames";

export const AMONG_VIEWS = [HomeViewAs.Tile, HomeViewAs.Table, HomeViewAs.Gain, HomeViewAs.Trend];

type TooltipOption = {
    id: ID;
    name: string;
}

const MAX_RESULTS = 16;

const TooltipOptionsList: FCX<{
    data: TooltipOption[];
}> = ({
    data,
    className,
}) => {

    return (
        <div className={cn("TooltipOptionsList", className)}>
            {data.slice(0, MAX_RESULTS).map((option) => (
                <div
                    key={`TooltipOption${option.id}`}
                    className="TooltipOptionsList__option"
                >
                    {option.name}
                </div>
            ))}
            {data.length > MAX_RESULTS && (
                <div
                    key="TooltipOptionMore"
                    className="TooltipOptionsList__option"
                >
                    ...and {data.length - MAX_RESULTS} more
                </div>
            )}
        </div>
    );
}

function useGetAmongSettingsSummary() {
    const {
        restaurants = [],
        cpgBrands = [],
        cStores = [],
        hotels = [],
        segments = [],
        onSites = [],
        supermarkets = [],
        deliveryServices = [],
    } = useCommonDataContext();
    const respondentTypesGroups = useRespondentTypesGroups({
        isEatingHabitsExtended: true,
    });

    return useCallback(
        (data: HomeAiSearchSettings['among'] | HomeAiSearchSettings['indexTo']) => {
            const amongItems: SettingsValue[] = [];

            const dataValuesMap: Array<{
                title: string;
                items: Array<{ id: ID; name: string; }>;
                values: ID[];
            }> = [
                { title: 'Restaurants', items: restaurants, values: data.restaurants || [] },
                { title: 'C-Stores', items: cStores, values: data.cStores || [] },
                { title: 'Supermarkets', items: supermarkets, values: data.supermarkets || [] },
                { title: 'Hotels', items: hotels, values: data.hotels || [] },
                { title: 'Brands', items: cpgBrands, values: data.brands || [] },
                { title: 'Segments', items: segments, values: data.segmentsAndOnSites || [] },
                { title: 'On-Sites', items: onSites, values: data.segmentsAndOnSites || [] },
                { title: 'Delivery Services', items: deliveryServices, values: data.deliveryServices || [] },
            ];

            if (data.respondentTypes.length) {
                respondentTypesGroups.forEach(group => {
                    const selectedItems = group.items.filter(i => data.respondentTypes.includes(i.id));

                    if (selectedItems.length === 1) {
                        const [item] = selectedItems;
                        amongItems.push({
                            id: `among_${item.id}`,
                            title: `${group.name}${group.name ? ': ' : ''}${item.name}`,
                        });
                    } else if (selectedItems.length > 1) {
                        amongItems.push({
                            id: `among_${group.id}`,
                            title: `${group.name} (${selectedItems.length})`,
                            tooltip: (<TooltipOptionsList
                                    data={selectedItems}
                                />
                            )
                        });
                    }
                });
            }

            dataValuesMap.forEach(({ title, values, items }) => {
                if (values && values.length) {
                    const selectedItems = items.filter(i => values.includes(i.id));

                    if (selectedItems.length === 1) {
                        const [item] = selectedItems;
                        amongItems.push({
                            id: `among_${title}`,
                            title: `${title}: ${item.name}`,
                        });
                    } else if (selectedItems.length > 1) {
                        amongItems.push({
                            id: `among_${title}`,
                            title: `${title} (${selectedItems.length})`,
                            tooltip: (
                                <TooltipOptionsList
                                    data={selectedItems}
                                />
                            )
                        });
                    }
                }
            });

            return amongItems;
        },
        [
            restaurants,
            cStores,
            supermarkets,
            hotels,
            cpgBrands,
            segments,
            onSites,
            deliveryServices,
            respondentTypesGroups,
        ]
    );
}

export function useSettingsSummary(settings: HomeAiSearchResponseModel['settings']) {
    const getAppliedCategoryNamesData = useGetAppliedCategoryNamesData();
    const getMacStage = useGetMacStage();
    const getHeatScore = useGetHeatScore();
    const getMetric = useGetMetric();
    const getAmongSettingsSummary = useGetAmongSettingsSummary();
    const { affinityAmong = [] } = useCommonDataContext();

    return useMemo(
        (): AiResultProps['settings'] => {
            const result: AiResultProps['settings'] = [];

            if (!settings) {
                return result;
            }

            const {
                viewAs,
                show,
                filters,
                semanticSearch,
                among,
                indexTo,
                displayMetric,
                sortByHeatScore,
                sortByAffinityAmong,
                sortByMetric,
            } = settings;

            result.push({ title: 'View As', value: viewAs, });
            result.push({ title: 'Show', value: show, });

            const filterValues: SettingsValue[] = [];

            if (filters.categories?.length) {
                const selectedCategoriesData = getAppliedCategoryNamesData(filters.categories);

                if (selectedCategoriesData.length === 1) {
                    const [c] = selectedCategoriesData;
                    filterValues.push({
                        id: 'categories',
                        title: `${c.name}${c.isAllSelected ? '' : ' (' + c.subCategories.length + ')'}`,
                        tooltip: c.isAllSelected ? '' : (
                            <TooltipOptionsList
                                data={c.subCategories.map(subCat => ({
                                    id: subCat,
                                    name: subCat,
                                }))}
                            />
                        ),
                    });
                } else {
                    filterValues.push({
                        id: 'categories',
                        title: `Categories (${selectedCategoriesData.length})`,
                        tooltip: (
                            <TooltipOptionsList
                                data={selectedCategoriesData.map(c => ({
                                    id: c.id,
                                    name: `${c.name}${c.isAllSelected ? '' : ' (' + c.subCategories.length + ')'}`,
                                }))}
                            />
                        )
                    });
                }
            }

            if (filters.macStages?.length) {
                filterValues.push({
                    id: 'macStages',
                    title: `MAC Stages (${filters.macStages.length})`,
                    tooltip: (
                        <TooltipOptionsList
                            data={filters
                                .macStages.map(getMacStage)
                                .map(i => ({ id: i?.id ?? 0, name: i?.name ?? '' }))
                            }
                        />
                    ),
                });
            }

            if (filters.heatScores?.length) {
                filterValues.push({
                    id: 'heatScore',
                    title: `Heat Score (${filters.heatScores.length})`,
                    tooltip: (<TooltipOptionsList
                            data={filters
                                .heatScores.map(getHeatScore)
                                .map(i => ({ id: i?.id ?? 0, name: i?.name ?? '' }))
                            }
                        />
                    ),
                });
            }

            if (filterValues.length) {
                result.push({
                    title: 'Filters',
                    values: filterValues,
                });
            }

            if (displayMetric) {
                result.push({
                    title: 'Display',
                    value: getMetric(settings.displayMetric)?.name ?? '',
                });
            }

            const amongItems = getAmongSettingsSummary(among);
            const indexToItems = getAmongSettingsSummary(indexTo);

            if (amongItems.length) {
                result.push({
                    title: AMONG_VIEWS.includes(viewAs) ? 'Among' : 'For',
                    values: amongItems,
                });
            }

            if (indexToItems.length) {
                result.push({
                    title: 'Index to',
                    values: indexToItems,
                });
            }

            if (semanticSearch) {
                result.push({
                    title: 'Semantic Filter',
                    value: semanticSearch,
                });
            }

            let sortingItem: string | null = null;

            if (sortByHeatScore) {
                sortingItem = `Heat Score`;
            } else if (sortByAffinityAmong) {
                const affinityAmongMetric = affinityAmong.find(
                    a => a.id === sortByAffinityAmong
                );
                if (affinityAmongMetric) {
                    sortingItem = affinityAmongMetric.name;
                }
            } else if (sortByMetric) {
                const metric = getMetric(sortByMetric);
                if (metric) {
                    sortingItem = metric.name;
                }
            }

            if (sortingItem) {
                result.push({
                    title: 'Sort by',
                    value: sortingItem,
                });
            }

            return result;
        },
        [
            settings,
            getAppliedCategoryNamesData,
            getMacStage,
            getHeatScore,
            getMetric,
            getAmongSettingsSummary,
            affinityAmong,
        ]
    )
}